import React from 'react';

function EngineeringPhilosophy() {
  const principles = [
    {
      title: "Write for Humans First",
      desc: "Computers execute the code. Future developers (often yourself in 6 months) maintain it. Write simple, readable, and well-commented code."
    },
    {
      title: "Simplicity Scales",
      desc: "Complexity grows naturally, but simplicity must be actively designed. Simple systems have fewer failure points and are much easier to debug under load."
    },
    {
      title: "Measure, Then Optimize",
      desc: "Every performance optimization needs evidence. Measure latency, throughput, CPU, and DB queries before writing complex optimizations."
    },
    {
      title: "Future You is Another Engineer",
      desc: "Treat them well. Document architectures, draw sequence diagrams, write setup guides. Good documentation is core engineering."
    }
  ];

  const learningList = [
    { name: "Apache Kafka", status: "In progress" },
    { name: "Kubernetes Orchestration", status: "Hands-on learning" },
    { name: "System Design Patterns", status: "Deep-diving case studies" },
    { name: "Redis Internals & Memory Architecture", status: "Advanced concepts" },
    { name: "Spring Cloud Microservices", status: "Exploring patterns" }
  ];

  const books = [
    {
      title: "Designing Data-Intensive Applications",
      author: "Martin Kleppmann",
      desc: "The bible for understanding distributed storage, consistency, and replication paradigms."
    },
    {
      title: "Clean Code",
      author: "Robert C. Martin",
      desc: "Foundational principles for writing software that is readable, clean, and maintainable."
    },
    {
      title: "System Design Interview",
      author: "Alex Xu",
      desc: "Excellent guide for structuring scalability solutions for millions of active users."
    }
  ];

  return (
    <section id="philosophy" className="py-24 md:py-32 bg-primary/5 dark:bg-gray-900/40 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-bold text-accent uppercase tracking-wider">How I Think</span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mt-2 mb-4 relative inline-block">
            Engineering Philosophy
            <span className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-accent rounded-full"></span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto mt-8 text-gray-600 dark:text-gray-300">
            Software engineering is not about writing code; it is about making good structural and trade-off decisions over time.
          </p>
        </div>

        {/* 2-Column Main Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          
          {/* Core Principles (Takes 2 Cols) */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
              <span className="w-8 h-0.5 bg-accent mr-3"></span>
              Core Principles
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {principles.map((pr, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700/60 hover:shadow-md transition-shadow duration-300">
                  <div className="text-primary dark:text-accent font-bold text-lg mb-2">0{idx + 1}. {pr.title}</div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{pr.desc}</p>
                </div>
              ))}
            </div>

            {/* Differentiator Quote */}
            <div className="bg-white dark:bg-gray-800/80 rounded-xl p-8 border-l-4 border-accent shadow-sm mt-6">
              <p className="text-lg italic text-gray-700 dark:text-gray-200">
                "Many developers build features; I focus on building reliable systems. Instead of asking 'does it work?', I ask 'how does it behave under load and failure?'"
              </p>
            </div>
          </div>

          {/* What I'm Learning Column */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-100 dark:border-gray-700/60 shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
              <span className="w-6 h-0.5 bg-accent mr-3"></span>
              Currently Improving
            </h3>
            
            <ul className="space-y-6">
              {learningList.map((learn, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="text-accent mr-3 mt-1">✦</span>
                  <div>
                    <h4 className="font-bold text-gray-800 dark:text-gray-200 text-sm">{learn.name}</h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{learn.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recommended Books Section */}
        <div className="border-t border-gray-200 dark:border-gray-800 pt-16">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center flex items-center justify-center">
            <span className="w-8 h-0.5 bg-accent mr-3"></span>
            Books Shaping My Engineering Mindset
            <span className="w-8 h-0.5 bg-accent ml-3"></span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {books.map((book, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700/60 flex flex-col justify-between hover:shadow-md transition-shadow">
                <div>
                  <span className="text-3xl mb-4 block">📚</span>
                  <h4 className="font-bold text-lg text-primary dark:text-white mb-1">{book.title}</h4>
                  <span className="text-xs text-gray-400 font-medium">By {book.author}</span>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mt-3 leading-relaxed">{book.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default EngineeringPhilosophy;
